import { CHAT_MARKER_KINDS, CHAT_MARKER_TAG_NAMES, chatMarkerHref, isChatMarkerKind, type ChatMarker } from "./chatMarkers"

export interface RemarkChatMarkersOptions {
  kinds: readonly ChatMarker["kind"][]
}

type MarkerKinds = ReadonlySet<ChatMarker["kind"]>

interface MarkdownNode {
  type: string
  value?: string
  url?: string
  children?: MarkdownNode[]
}

interface MarkerSpan extends ChatMarker {
  consumedSiblings: number
}

const MARKER_OPEN_TAG = new RegExp(`^<(${CHAT_MARKER_TAG_NAMES})\\s+id="([^"]+)"\\s*(/?)>$`)
const MARKER_CLOSE_TAG = new RegExp(`^</(${CHAT_MARKER_TAG_NAMES})\\s*>$`)
const MARKER_TAG_PAIR = new RegExp(`^<(${CHAT_MARKER_TAG_NAMES})\\s+id="([^"]+)"\\s*>\\s*</\\1\\s*>$`)
const SELF_CLOSING_SLASH = "/"
const BLOCK_PARENTS: ReadonlySet<string> = new Set(["root", "blockquote", "listItem", "footnoteDefinition"])

function isWhitespaceText(node: MarkdownNode): boolean {
  return node.type === "text" && (node.value ?? "").trim() === ""
}

function htmlValue(node: MarkdownNode | undefined): string | null {
  return node && node.type === "html" && typeof node.value === "string" ? node.value.trim() : null
}

function markerSpanAt(siblings: MarkdownNode[], index: number, kinds: MarkerKinds): MarkerSpan | null {
  const value = htmlValue(siblings[index])
  if (value === null) return null
  const pair = MARKER_TAG_PAIR.exec(value)
  if (pair && isChatMarkerKind(pair[1]) && kinds.has(pair[1])) return { kind: pair[1], id: pair[2], consumedSiblings: 1 }
  const open = MARKER_OPEN_TAG.exec(value)
  if (!open || !isChatMarkerKind(open[1]) || !kinds.has(open[1])) return null
  if (open[3] === SELF_CLOSING_SLASH) return { kind: open[1], id: open[2], consumedSiblings: 1 }
  let closerIndex = index + 1
  while (closerIndex < siblings.length && isWhitespaceText(siblings[closerIndex])) closerIndex += 1
  const closerValue = htmlValue(siblings[closerIndex])
  const close = closerValue === null ? null : MARKER_CLOSE_TAG.exec(closerValue)
  if (!close || close[1] !== open[1]) return null
  return { kind: open[1], id: open[2], consumedSiblings: closerIndex - index + 1 }
}

function markerLinkNode(marker: ChatMarker, inBlockParent: boolean): MarkdownNode {
  const link: MarkdownNode = { type: "link", url: chatMarkerHref(marker), children: [] }
  return inBlockParent ? { type: "paragraph", children: [link] } : link
}

function replaceMarkersIn(node: MarkdownNode, kinds: MarkerKinds): void {
  if (!node.children) return
  const inBlockParent = BLOCK_PARENTS.has(node.type)
  const replaced: MarkdownNode[] = []
  for (let index = 0; index < node.children.length; ) {
    const marker = markerSpanAt(node.children, index, kinds)
    if (marker) {
      replaced.push(markerLinkNode(marker, inBlockParent))
      index += marker.consumedSiblings
      continue
    }
    replaced.push(node.children[index])
    index += 1
  }
  node.children = replaced
  node.children.forEach((child) => replaceMarkersIn(child, kinds))
}

export function remarkChatMarkers(options?: RemarkChatMarkersOptions) {
  const kinds: MarkerKinds = new Set(options?.kinds ?? CHAT_MARKER_KINDS)
  return (tree: MarkdownNode): void => {
    replaceMarkersIn(tree, kinds)
  }
}
